import { ApolloError, useMutation } from '@apollo/client';
import { CREATE_VEHICLE } from '@mutations/createVehicle';
import { GET_CUSTOMER } from '@queries/getCustomer';
import { useNavigation, useRoute } from '@react-navigation/native';
import VehicleForm from 'components/account/vehicle/VehicleForm';
import { showToast } from 'components/common/CustomToast';
import AccountContext from 'context/AccountContext';
import useAfterAuthenticationRedirect from 'hooks/useAfterAuthenticationRedirect';
import useAfterVehicleRedirect from 'hooks/useAfterVehicleRedirect';
import useUpdateCustomerMutation from 'hooks/useUpdateCustomerMutation';
import React, { useContext } from 'react';
import { parkingVehicleLicensePlate } from 'states/common';
import { useParkingStore } from 'stores/parkingStore';
import { useVehicleStore } from 'stores/vehicleStore';
import {
  Mutation,
  MutationCreateVehicleArgs,
} from 'types/generatedSchemaTypes';
import { RootNavProps } from 'types/navigation';
import { openTutorial } from 'utils/tutorialUtils';

const VehicleAdd: React.FC = () => {
  const { setParkingVehiclePlate } = useParkingStore((state) => state);
  const { goBack } = useNavigation<RootNavProps>();
  const { customer } = useContext(AccountContext);
  const { name: routeName } = useRoute();
  const isOnboarding = routeName === 'addVehicleOnboarding';
  const selectCreatedVehicle = routeName === 'addVehicleParking';
  const { navigateAfterVehicleSaved } = useAfterVehicleRedirect();
  const { skipOnboarding } = useAfterAuthenticationRedirect();
  const { updateCustomer } = useUpdateCustomerMutation({ skipRefetch: true });
  const { vehicleData: newVehicle, resetVehicleStore } = useVehicleStore(
    (state) => state
  );

  const proceedToNextStep = async () => {
    if (isOnboarding) {
      await skipOnboarding();
    } else {
      if (!goBack) {
        navigateAfterVehicleSaved();
      }
      goBack();
    }
  };

  const checkAlwaysSelectVehicle = (vehicleCount: number) => {
    if (vehicleCount === 1 || vehicleCount === 2) {
      updateCustomer({
        variables: {
          input: {
            applicationPreferences: {
              alwaysSelectVehicle: vehicleCount === 1,
            },
          },
        },
        onCompleted: () => {
          if (vehicleCount === 2) {
            openTutorial('automaticVehicleSelection');
          }
        },
      });
    }
  };

  const [createVehicle, { loading }] = useMutation<
    Mutation,
    MutationCreateVehicleArgs
  >(CREATE_VEHICLE, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GET_CUSTOMER,
        variables: {
          input: {
            fetchDigitalPermits: true,
          },
        },
        errorPolicy: 'all',
      },
    ],
    onCompleted: async ({
      createVehicle: {
        licensePlate: { plateText },
      },
    }) => {
      showToast('success.vehicleAdded', 'success');
      await proceedToNextStep();
      const vehicleCount = customer.vehicles?.length ?? 0;
      if ((selectCreatedVehicle || vehicleCount === 1) && plateText) {
        // Parking logic is moved to zustand store, before we can move everything to store,
        // we need to update the parking state and also the global reactive var
        setParkingVehiclePlate(plateText);
        parkingVehicleLicensePlate(plateText);
      }
      checkAlwaysSelectVehicle(vehicleCount);
      resetVehicleStore();
    },
    onError: async (error: ApolloError) => {
      if (
        error.graphQLErrors.some(
          (e) => e.extensions?.code === 'VEHICLE_ALREADY_ADDED'
        )
      ) {
        showToast('error.vehicleAlreadyAdded', 'error');
        await proceedToNextStep();
        return;
      }
      showToast('error.addVehicle', 'error');
    },
  });

  const handleSubmit = async () => {
    const countryCodes =
      newVehicle?.cameraRecognition?.countryCodes ??
      (customer.countryCode ? [customer.countryCode] : []);

    await createVehicle({
      variables: {
        input: {
          licensePlate: {
            plateText: newVehicle.licensePlate?.plateText ?? '',
            countryCode: newVehicle.licensePlate?.countryCode ?? undefined,
          },
          name: newVehicle.name,
          cameraRecognition: {
            allowed: newVehicle.cameraRecognition?.allowed ?? false,
            enabled: newVehicle.cameraRecognition?.enabled ?? false,
            countryCodes: countryCodes,
          },
          appSettings: {
            icon: newVehicle.appSettings?.icon,
            color: newVehicle.appSettings?.color,
            type: newVehicle.appSettings?.type,
            evPlugType: newVehicle.appSettings?.evPlugType,
          },
        },
      },
    });
  };
  return (
    <VehicleForm
      isNew={true}
      isOnboarding={isOnboarding}
      onSubmit={handleSubmit}
      loading={loading}
    />
  );
};

export default VehicleAdd;
